import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { CandidateList } from 'common/models/Candidates/CandidateList';
import { MergeCandidates } from 'services/CandidatesService';
import { JobTracking } from 'common/models/Jobs/JobTracking';
import GridWithStateWrapper from 'components/GridWidthStateWrapper';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import PreviewLoaderComponent from 'components/Previews/PreviewLoader';
import IconButton from '@mui/material/IconButton';

interface Props {
  open: boolean;
  candidates: CandidateList[] | JobTracking[];
  recordType: 'candidate-list' | 'job-tracking';
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

type columnGetterFunc = (tagHoverHandler: (params: GridRenderCellParams) => any) => GridColDef[];

const dateValueGetter: GridValueGetter<CandidateList, any, undefined, string> = (value) => {
  if (value) {
    const m = moment(value);
    if (m.isValid() && m.get('year') > 1) {
      return m.toDate();
    }
  }
};

const dateRenderer = (params: GridRenderCellParams) => {
  if (params.value) {
    return moment(params.value).format('DD MMM YYYY');
  }
  return 'Never';
};

const nameValueGetter: GridValueGetter<CandidateList, any, undefined, string> = (value) => {
  if (value && value.trim() !== '') {
    return value;
  }

  return '-----';
};

const numberRenderer = (params: GridRenderCellParams) => {
  return params.value.toLocaleString('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

const getCandidateListColumns: columnGetterFunc = (
  tagsRenderer: (params: GridRenderCellParams) => any
) => {
  return [
    { headerName: 'ID', field: 'id', width: 75 },
    {
      headerName: 'Name',
      field: 'fullName',
      width: 250,
      valueGetter: nameValueGetter,
    },
    {
      headerName: 'Email',
      field: 'emailAddress',
      headerAlign: 'center',
      align: 'center',
      width: 250,
    },
    {
      headerName: 'Mobile',
      field: 'mobile',
      headerAlign: 'center',
      align: 'center',
      width: 250,
    },
    {
      headerName: 'Tags',
      field: 'tags',
      width: 70,
      headerAlign: 'center',
      align: 'center',
      renderCell: tagsRenderer,
      editable: false,
    },
    { headerName: 'Job Title', field: 'jobTitle', width: 200 },
    { headerName: 'Employer', field: 'employer', width: 200 },
    { headerName: 'Location', field: 'location', width: 200 },
    {
      headerName: 'Job Type',
      field: 'preferredEmploymentType',
      headerAlign: 'center',
      align: 'center',
      width: 150,
    },
    {
      headerName: 'Available',
      field: 'available',
      headerAlign: 'center',
      align: 'center',
      width: 125,
    },
    {
      headerName: 'Preferred Salary',
      field: 'preferredSalary',
      headerAlign: 'center',
      align: 'center',
      width: 125,
    },
    {
      headerName: 'Preferred Rate',
      field: 'preferredRate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
    },
    {
      headerName: 'Last Updated',
      field: 'compareDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    {
      headerName: 'Last Activity',
      field: 'lastActivityDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    {
      headerName: 'Last Contact',
      field: 'lastContactDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    { headerName: 'Score', field: 'score', width: 100, renderCell: numberRenderer },
  ];
};

const getJobTrackingColumns: columnGetterFunc = (
  tagsRenderer: (params: GridRenderCellParams) => any
) => {
  return [
    { headerName: 'ID', field: 'id', width: 75 },
    { headerName: 'Rating', field: 'ratingID' },
    {
      headerName: 'Name',
      field: 'fullName',
      width: 250,
      valueGetter: nameValueGetter,
    },
    {
      headerName: 'Email',
      field: 'emailAddress',
      headerAlign: 'center',
      align: 'center',
      width: 250,
    },
    {
      headerName: 'Mobile',
      field: 'mobile',
      headerAlign: 'center',
      align: 'center',
      width: 250,
    },
    {
      headerName: 'Tags',
      field: 'tags',
      width: 70,
      headerAlign: 'center',
      align: 'center',
      renderCell: tagsRenderer,
      editable: false,
    },
    { headerName: 'Job Title', field: 'jobTitle', width: 200 },
    { headerName: 'Employer', field: 'employer', width: 250 },
    { headerName: 'Location', field: 'location', width: 200 },
    {
      headerName: 'Available',
      field: 'availabilityDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    {
      headerName: 'Status Updated',
      field: 'statusUpdatedDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    {
      headerName: 'Preferred Salary',
      field: 'preferredSalary',
      headerAlign: 'center',
      align: 'center',
      width: 125,
    },
    {
      headerName: 'Preferred Rate',
      field: 'preferredRate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
    },
    {
      headerName: 'Last Updated',
      field: 'compareDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    {
      headerName: 'Last Activity',
      field: 'lastActivityDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
    {
      headerName: 'Last Contact',
      field: 'lastContactDate',
      headerAlign: 'center',
      align: 'center',
      width: 125,
      valueGetter: dateValueGetter,
      renderCell: dateRenderer,
      type: 'date',
    },
  ];
};

export default function MergeCandidatesDialog({
  open,
  candidates,
  recordType,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showTagsPrevewNoDelay, setShowTagsPreviewNoDelay] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewEntityType | ''>('');
  const [previewRecordId, setPreviewRecordId] = useState(0);
  const [isPreviewTags, setIsPreviewTags] = useState(false);
  const [previewRecordName, setPreviewRecordName] = useState('');
  const apiRef = useGridApiRef();

  const columns = useMemo<GridColDef[]>(() => {
    const handlePreviewHover = (
      type: PreviewEntityType | '',
      id: number,
      isTags: boolean = false,
      recordName: string = '',
      noDelay: boolean = false
    ) => {
      setPreviewType(type);
      setPreviewRecordId(id);
      setIsPreviewTags(isTags);
      setPreviewRecordName(recordName);
      setIsPreviewOpen(true);
      setShowTagsPreviewNoDelay(noDelay);
    };

    const handlePreviewClose = () => {
      setIsPreviewOpen(false);
      setShowTagsPreviewNoDelay(false);
    };

    const tagsRenderer = (params: GridRenderCellParams) => {
      return (
        <IconButton
          size="small"
          onMouseEnter={() =>
            handlePreviewHover('candidate', params.row.id, true, params.row.fullName)
          }
          onMouseLeave={handlePreviewClose}
          onClick={() =>
            handlePreviewHover('candidate', params.row.id, true, params.row.fullName, true)
          }
        >
          {params.value ? (
            <LocalOfferIcon style={{ color: '#f209a6' }} />
          ) : (
            <LocalOfferOutlinedIcon />
          )}
        </IconButton>
      );
    };

    if (recordType === 'candidate-list') return getCandidateListColumns(tagsRenderer);
    if (recordType === 'job-tracking') return getJobTrackingColumns(tagsRenderer);

    return [];
  }, [recordType]);

  const mergeHandlerCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    const candidateIds = candidates.map((c) => c.id);
    const res = await MergeCandidates(candidateIds, errorHandler);
    if (res) {
      successHandler &&
        successHandler(
          'Records have been queued for merging. This process can take upto 24 hours.'
        );
      closeHandler();
    }
    loadingHandler && loadingHandler(false);
  }, [closeHandler, errorHandler, loadingHandler, successHandler, candidates]);

  const gridName = useMemo(() => {
    if (recordType === 'candidate-list') return 'merge-dialog-candidate-list';
    if (recordType === 'job-tracking') return 'merge-dialog-job-tracking';
    return '';
  }, [recordType]);

  return (
    <>
      <PreviewLoaderComponent
        open={isPreviewOpen}
        entityType={previewType}
        recordId={previewRecordId}
        isTagsPreview={isPreviewTags}
        showDelayMs={showTagsPrevewNoDelay ? 0 : undefined}
        titleOverride={previewRecordName}
        kioskToken={null}
      />
      <Dialog open={open} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '645px' } }}>
        <DialogTitle>Candidates Merge</DialogTitle>
        <DialogContent sx={{ display: 'flex' }}>
          <div
            style={{ paddingTop: '10px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}
          >
            Selected records
            <GridWithStateWrapper
              gridName={gridName}
              hideFooter
              columns={columns}
              rows={candidates}
              apiRef={apiRef}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeHandler}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={mergeHandlerCallback}>
            Merge
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
